import React, { useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import PushToggle from './PushToggle';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const payload = {
        notificationType: "email",
        contact: email
      };
      
      await axios.post(`${API_BASE_URL}/api/notifications/subscribe`, payload);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const errorMessage = err.response.data.error;
        setError(errorMessage || 'Failed to subscribe. Please try again.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (


    <div className="fixed inset-0 bg-black/50 flex justify-center items-start z-50">
          <AnimatePresence mode="sync">
    <motion.div
      key={"notification"} 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
      <div className="bg-surface text-content rounded-lg ring-1 ring-line p-6 w-full max-w-md text-sm md:text-base">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Gameweek Deadline reminders</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-content-muted hover:text-content hover:bg-surface-sunken transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        {success ? (
          <div className="mb-4 rounded p-2 text-green-800 bg-green-100 dark:text-green-300 dark:bg-green-500/15">
            Successfully subscribed to deadline notifications!
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="mb-4">
              Get notified about each gameweek deadline.
            </p>

            <PushToggle />

              <div className="mb-4">
                <label className="block mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full min-h-11 p-2 bg-surface text-content placeholder:text-content-muted border border-line rounded focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="your@email.com"
                />
              </div>

            {error && (
              <div className="mb-4 rounded p-2 text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/25">
                {error}
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="mr-2 inline-flex min-h-11 items-center px-4 py-2 rounded-md text-content-secondary hover:text-content hover:bg-surface-sunken transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-h-11 items-center px-4 py-2 bg-inverse text-inverse-content rounded font-medium hover:opacity-90 disabled:opacity-50 transition"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
          </form>
        )}
      </div>
      </motion.div>
      </AnimatePresence>
    </div>
   
  );
};